import { useEffect } from "react";
import { useT } from "../i18n";
import { usePreis } from "../daten/haken";
import { alsMerkzuschlag, useMerkliste, zuschlagNetto, type Merkposten } from "../daten/merkliste";
import {
  abholungAllerStand,
  abholungFuerAlle,
  abholungMoeglich,
  abholungWeichtAb,
  abholungUmlegen,
  anhaengerNachUmlegen,
  anhaengerNochGebraucht,
  anhaengerWunsch,
  mitgebracht,
} from "../daten/abholung";

/* ══════════════════════════════════════════════════════════════════
   „Kunde holt ab" — der Schalter an der Sammelkarte.

   Luca, 18.09.2026: *„Da muss man … hier in der Merkliste dann direkt
   einen Schnellwahlbutton haben: ‚Kunde holt ab' oder ‚Selbstabholung'
   als Button mit einer Checkbox. Das im Hintergrund löscht dann einfach
   die Anlieferung mit 65 €."*

   **Er steht nur, wo er eine Frage ist.** Ein 25-m-Teleskoplader wird
   nicht abgeholt, und ein Schalter, der bei jedem Posten dasteht und bei
   den meisten nicht gilt, wird nicht gelesen (28-designkonzept.md).
   Woher das kommt, sagt der Stamm: `machine_category.self_pickup`, am Typ
   überschreibbar (Migration 0127) — dieselbe Vererbung wie die
   Bedienungspflicht. **Und seit der Systemprüfung (A-02, 24.09.2026)
   auch die Karte selbst:** Steht an ihr ein Zuschlag, der bei
   Selbstabholung entfällt, fällt eine Fahrt an, und wer sie sparen will,
   braucht den Schalter (`abholungMoeglich`).

   **Was er tut, steht in `daten/abholung.ts`** und nicht hier: Die
   Anlieferung wird abgewählt, der vorgeschlagene Anhänger kommt als
   eigener Posten dazu. Hier steht nur, wie er aussieht und wann er
   nachfragt.

   ── Warum der Anhänger seinen Preis hier bekommt ──────────────────

   Er ist ein **Gerät** und keine Zuschlagszeile (0102, Regel 8): eigene
   Position, eigene Belegung, eigener Satz. Den holt dieselbe
   Preisauskunft wie bei jedem anderen Posten — gefragt wird erst, wenn
   der Schalter angeht, und nur dann. Ein Posten ohne Betrag stünde sonst
   als „Preis offen" in der Sammlung, und genau die Zahl ist die, um die
   es Luca ging: *„dann wird diese 15-€-Anhängerpauschale mit
   reingerechnet."*

   ── Und warum die Lage hier nicht neu gefragt wird ────────────────

   Der Schalter ändert weder Typ noch Zeitraum dieses Postens: Dieselbe
   Maschine steht an denselben Tagen auf dem Hof, ob wir fahren oder der
   Kunde. Eine zweite Frage nach der Lage bekäme dieselbe Antwort.

   **Der Anhänger dagegen ist eine neue Maschine**, und ob der im Fenster
   frei ist, weiß niemand. Er bekommt deshalb seine eigene Karte, und die
   sieht ungefragt nach (`Sammelkarte`, `nachsehen` bei `wegen`) — ein
   Anhänger, der am Dienstag schon unterwegs ist, ist genau das, was man
   nicht zusagen darf.

   Klassen: `ml-abholung*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/**
 * Der Schalter, wenn er dazugehört — sonst nichts.
 *
 * **Der Anhänger selbst trägt keinen.** Er wird abgeholt, weil die Maschine
 * abgeholt wird; ein zweiter Schalter daneben wäre ein Widerspruch in einer
 * Liste (`wegen`).
 */
export function Abholung({ p, danach }: { p: Merkposten; danach?: (bedienerWeg: boolean) => void }) {
  if (!abholungMoeglich(p)) return null;
  return <Schalter p={p} {...(danach ? { danach } : {})} />;
}

/**
 * **Den Schalter an einem Posten umlegen** — eine Stelle für die Zeile, das
 * Blatt und den Kopf: die Umlegung aus `daten/abholung.ts`, und beim
 * Ausschalten geht ein mitgenommener Anhänger mit weg, wenn ihn kein anderer
 * Posten braucht. `danach` erfährt, ob „mit Bediener" dabei aufgehoben wurde
 * — mit oder ohne Bediener ist ein anderer Preis (0096), die Merkliste
 * rechnet dann nach.
 */
export function useAbholungUmlegen(): (p: Merkposten, neu: boolean, danach?: (bedienerWeg: boolean) => void) => void {
  const { posten, aendern, entfernen } = useMerkliste();
  return (p, neu, danach) => {
    const teil = abholungUmlegen(p, neu);
    aendern(p.id, teil);
    if (anhaengerNachUmlegen(neu) === "weg") {
      const anhaenger = mitgebracht(posten, p, "abholung");
      if (anhaenger && !anhaengerNochGebraucht(posten, anhaenger, p.id)) entfernen(anhaenger.id);
    }
    danach?.(teil.bedient === false);
  };
}

/**
 * **„Kunde holt ab" im Kopf des Abschlusses** (Lisas Durchgang, 08.10.2026)
 * — neben „Wohin?", für alle Posten mit Transport, auch bei einem einzigen.
 * Bis dahin stand der Schalter über den Posten erst ab zwei abholbaren
 * („Kunde holt alles ab"); bei einem einzigen Minibagger war er nur noch in
 * der aufgeklappten Zeile unter „Transport" — und „Preis fehlt: Anlieferung"
 * hielt den Abschluss auf, obwohl der Kunde abholte.
 *
 * Er zeigt die Wahl für alle (`kopf`); ein Posten, der abweicht, sagt es an
 * seiner Zeile (`AbholungAbweichung`). Umlegen setzt alle Posten mit
 * Transport (`abholungFuerAlle`).
 */
export function AbholungKopf({ posten, kopf, setzen, telefon = false }: {
  posten: Merkposten[];
  kopf: boolean;
  setzen: (an: boolean) => void;
  telefon?: boolean;
}) {
  const t = useT();
  if (abholungAllerStand(posten) === null) return null;
  return (
    <label className={`ml-abholung-schalter ma-holtab${telefon ? " telefon" : ""}`} title={t("merkliste.holtAbKopfWas")}>
      <input type="checkbox" checked={kopf} onChange={(e) => setzen(e.target.checked)} />
      <span>{t("merkliste.holtAb")}</span>
    </label>
  );
}

/** Was der Kopf für alle tut — die Umlegung je Posten und die Anhänger, die beim Ausschalten gehen. */
export function useAbholungFuerAlle(): (posten: Merkposten[], an: boolean) => void {
  const { aendern, entfernen } = useMerkliste();
  return (posten, an) => {
    const was = abholungFuerAlle(posten, an);
    for (const a of was.aendern) aendern(a.id, a.teil);
    for (const id of was.entfernen) entfernen(id);
  };
}

/**
 * **Ein Posten, der vom Kopf abweicht** — an seiner Zeile, ohne Aufklappen.
 * Die Marke sagt, was für ihn gilt („holt ab" bzw. „liefern"); ein Klick
 * macht ihn wieder wie alle. Weicht er nicht ab, steht nichts da.
 */
export function AbholungAbweichung({ p, kopf, telefon = false }: { p: Merkposten; kopf: boolean; telefon?: boolean }) {
  const t = useT();
  const umlegen = useAbholungUmlegen();
  if (!abholungWeichtAb(p, kopf)) return null;
  const holtAb = p.selbstabholung === true;
  return (
    <button
      type="button"
      className={`ma-abweichung${telefon ? " telefon" : ""}`}
      title={t(kopf ? "merkliste.abweichungZurueckHoltAb" : "merkliste.abweichungZurueckLiefern")}
      aria-label={t(kopf ? "merkliste.abweichungZurueckHoltAb" : "merkliste.abweichungZurueckLiefern")}
      onClick={() => umlegen(p, kopf)}
    >
      {t(holtAb ? "merkliste.abweichungHoltAb" : "merkliste.abweichungLiefern")}
    </button>
  );
}

/**
 * **Der Umschalter in der Zeile der Merkliste** — klein, ohne Aufklappen
 * (Lisas Durchgang, 08.10.2026). Nur an Posten mit Transport; derselbe
 * Schalter wie unter „Transport" in der aufgeklappten Zeile.
 */
export function AbholungInZeile({ p, danach }: { p: Merkposten; danach?: (bedienerWeg: boolean) => void }) {
  const t = useT();
  const umlegen = useAbholungUmlegen();
  if (!abholungMoeglich(p)) return null;
  return (
    <label className="ml-holtab" title={t("merkliste.holtAb")}>
      <input
        type="checkbox"
        checked={p.selbstabholung === true}
        onChange={(e) => umlegen(p, e.target.checked, danach)}
      />
      <span>{t("merkliste.holtAbKurz")}</span>
    </label>
  );
}

function Schalter({ p, danach }: { p: Merkposten; danach?: (bedienerWeg: boolean) => void }) {
  const t = useT();
  const { posten, wer, merkenWennFrei, aendern, entfernen } = useMerkliste();
  const umlegenFuer = useAbholungUmlegen();
  const an = p.selbstabholung === true;

  /* **Der Preis des Anhängers — gefragt, solange der Schalter an ist.**
     `usePreis` fragt nur mit einem Typ; ohne Schalter steht dort nichts, und
     die Sammlung kostet keine Abfrage mehr als vorher.

     **Gesucht wird nach dem Anlass** (18.09.2026): Seit das Gespann (0072)
     dieselbe Mechanik benutzt, können an einem Posten zwei mitgebrachte
     hängen — der Transportanhänger und der Auflieger. Ohne `wegenArt` nähme
     dieser Schalter beim Ausmachen den falschen mit. */
  const anhaengerPosten = mitgebracht(posten, p, "abholung");
  const auskunft = usePreis(
    an && anhaengerPosten && !anhaengerPosten.gerechnet ? anhaengerPosten.typId : undefined,
    p.von,
    p.bis,
    wer.nr || null,
    false,
    anhaengerPosten?.varianteId || undefined,
  );
  const preis = auskunft.data;

  /* Die Pauschale steht an der Zeile, sobald die Antwort da ist. Vorher
     steht dort „Preis offen" — eine erfundene Null wäre schlimmer als eine
     Zeile, die sagt, dass sie noch wartet. */
  useEffect(() => {
    if (!preis || !anhaengerPosten || anhaengerPosten.gerechnet) return;
    const gerechnet = preis.zuschlaege.filter((z) => z.vorgewaehlt).map(alsMerkzuschlag);
    /* **Der Anhänger wird mit abgeholt** — er hängt hinten am Auto des
       Kunden. Auch bei ihm fällt die Anlieferung weg; stünde sie an seiner
       Zeile, wäre sie genau die Zahl, die der Schalter wegnehmen sollte.
       Dieselbe Umlegung wie am Gerät, damit es nur eine Regel dafür gibt. */
    const umgelegt = abholungUmlegen({ ...anhaengerPosten, zuschlaege: gerechnet }, true);
    aendern(anhaengerPosten.id, {
      gerechnet: true,
      miettage: preis.miettage,
      netto: preis.netto,
      einheiten: preis.menge,
      einheitKey: preis.einheit_key,
      zuschlaegeNetto: (umgelegt.zuschlaege ?? []).reduce((s, z) => s + zuschlagNetto(z), 0),
      ...umgelegt,
    });
  }, [preis, anhaengerPosten, aendern]);

  /**
   * **Der Anhänger — an und aus, für sich** (B8, 18.09.2026).
   *
   * Zurück heißt zurück, aber nur, solange niemand anders ihn noch braucht:
   * Zwei selbst abgeholte Maschinen teilen sich denselben Vorschlag.
   */
  const anhaengerSetzen = async (neu: boolean) => {
    if (neu) {
      /* **Nur, wenn er frei ist** (19.09.2026). Sonst kommt das Fenster, und
         der Haken bleibt leer — die Abholung selbst bleibt stehen: Ob der
         Kunde einen eigenen Anhänger mitbringt, ist eine Frage für den
         Anruf, nicht für die Sammlung. */
      const wunsch = anhaengerWunsch(p);
      if (wunsch) await merkenWennFrei(wunsch);
      return;
    }
    if (anhaengerPosten && !anhaengerNochGebraucht(posten, anhaengerPosten, p.id)) {
      entfernen(anhaengerPosten.id);
    }
  };

  /* „Kunde holt ab" an: nur die Frage nach dem Anhänger, kein Haken (N7-04).
     Aus: ein mitgenommener Anhänger geht mit weg. */
  const umlegen = (neu: boolean) => umlegenFuer(p, neu, danach);

  return (
    <div className="ml-abholung">
      <label className="ml-abholung-schalter">
        <input
          type="checkbox"
          checked={an}
          onChange={(e) => umlegen(e.target.checked)}
        />
        <span>{t("merkliste.holtAb")}</span>
      </label>
      {/* **Was der Schalter bewirkt hat, steht daneben** — nicht als Zahl,
          sondern als Satz. Wer am Telefon umschaltet, muss sagen können,
          was sich geändert hat, ohne die Summe nachzurechnen. */}
      {an ? <span className="ml-abholung-folge">{t("merkliste.holtAbOhneAnhaenger")}</span> : null}
      {/* ── Und der Anhänger wird gefragt, nicht untergeschoben ──────
          **B8, gewählt B** (18.09.2026): *„Wählt jemand ‚Kunde holt ab',
          fragt die Maske nach dem Anhänger und legt ihn als Posten an."*

          Bis hierher tat der Schalter beides stillschweigend in einem: Wer
          den Anhänger nicht brauchte, musste den Posten in der Liste suchen
          und wegnehmen. Jetzt steht die Frage da, wo sie entsteht — **ohne
          Haken** (N7-04, 05.10.2026; vorher vorbelegt): Dass der Kunde einen
          Anhänger braucht, folgt aus „holt ab" nicht, und ein vorbelegter
          Anhänger ging mit, wenn niemand hinsah (`anhaengerNachUmlegen`).

          **Und wo keiner hinterlegt ist, steht das auch da.** Vorher sagte
          die Zeile nur „Anlieferung entfällt" — wer selbst abholt und
          nichts zum Anhängen hat, stand ohne Wort da (Lisas Durchgang). */}
      {an ? (
        p.anhaenger ? (
          <label className="ml-abholung-schalter ml-abholung-anhaenger">
            <input
              type="checkbox"
              checked={Boolean(anhaengerPosten)}
              onChange={(e) => { void anhaengerSetzen(e.target.checked); }}
            />
            <span>{t("merkliste.anhaengerMitgeben", { was: p.anhaenger.typ })}</span>
          </label>
        ) : (
          <span className="ml-abholung-folge acht">{t("merkliste.anhaengerKeiner")}</span>
        )
      ) : null}
    </div>
  );
}
